/**
 * Balance Summary Card Component
 * Displays the current balance for a lease
 */

interface BalanceSummaryCardProps {
  balance: number;
  isLoading?: boolean;
}

export function BalanceSummaryCard({ balance, isLoading }: BalanceSummaryCardProps) {
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const isPositive = balance > 0;
  const isNegative = balance < 0;
  const isZero = balance === 0;

  const cardBg = isNegative ? 'bg-success/10' : isPositive ? 'bg-warning/10' : 'bg-accent/50';
  const cardBorder = isNegative ? 'border-success/20' : isPositive ? 'border-warning/20' : 'border-border';
  const amountColor = isNegative ? 'text-success' : isPositive ? 'text-warning' : 'text-foreground';
  const iconBg = isNegative ? 'bg-success' : isPositive ? 'bg-warning' : 'bg-muted-foreground/30';

  return (
    <div
      data-testid="balance-summary-card"
      className={`rounded-lg p-6 border ${cardBg} ${cardBorder}`}
    >
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-sm font-medium text-muted-foreground m-0">
            Current Balance
          </h3>
          {isLoading ? (
            <div className="text-3xl font-bold mt-2">
              <span className="text-muted-foreground">Loading...</span>
            </div>
          ) : (
            <div className="text-3xl font-bold mt-2">
              <span
                data-testid="balance-amount"
                className={`${amountColor} ph-no-capture`}
              >
                {formatCurrency(Math.abs(balance) / 100)}
              </span>
            </div>
          )}
          <p className="text-sm text-muted-foreground mt-1 m-0">
            {isPositive && 'Amount owed by tenant'}
            {isNegative && 'Credit in tenant favor'}
            {isZero && 'No outstanding balance'}
          </p>
        </div>
        <div
          className={`w-16 h-16 rounded-full ${iconBg} flex items-center justify-center text-3xl shrink-0`}
        >
          {isPositive && '💰'}
          {isNegative && '✅'}
          {isZero && '⚖️'}
        </div>
      </div>
    </div>
  );
}
